import { useState, useEffect } from "react"
import {
  Box,
  Flex,
  Text,
  Heading,
  Spinner,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  useColorModeValue,
  Button,
  Collapse,
  IconButton,
} from "@chakra-ui/react"
import { DownloadIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons"
import axios from "axios"
import ExcelJS from "exceljs"

const DcStock = ({ supplier, retailer }) => {
  const [loading, setLoading] = useState(false)
  const [summaryData, setSummaryData] = useState([])
  const [detailData, setDetailData] = useState([])
  const [expandedDc, setExpandedDc] = useState(null)
  const [downloading, setDownloading] = useState(false)

  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const headerBg = useColorModeValue("gray.50", "gray.700")
  const hoverBg = useColorModeValue("gray.50", "gray.700")
  const kpiBg = useColorModeValue("teal.50", "teal.900")

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    fetchData(controller.signal)
    return () => controller.abort()
  }, [supplier, retailer])

  const fetchData = async (signal) => {
    setLoading(true)
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/insights/dc-stock`,
        {
          supplier,
          retailer,
          userid: localStorage.getItem("userid"),
        },
        { signal }
      )
      if (res.data.status === 1) {
        setSummaryData(res.data.data.summary || [])
        setDetailData(res.data.data.detail || [])
      } else {
        setSummaryData([])
        setDetailData([])
      }
    } catch (err) {
      if (!axios.isCancel(err)) console.error("DcStock fetch error:", err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0
    return `R ${num.toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  }

  const formatNum = (val) => (parseInt(val) || 0).toLocaleString()

  const totalSohValue = summaryData.reduce((sum, r) => sum + (parseFloat(r.total_soh_value) || 0), 0)
  const totalSohQty = summaryData.reduce((sum, r) => sum + (parseInt(r.total_soh_qty) || 0), 0)
  const totalArticles = summaryData.reduce((sum, r) => sum + (parseInt(r.article_count) || 0), 0)

  const getDetailForDc = (siteCode) => detailData.filter((r) => r.site_code === siteCode)

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const workbook = new ExcelJS.Workbook()

      // Summary sheet
      const ws1 = workbook.addWorksheet("DC Summary")
      ws1.columns = [
        { header: "DC Code", key: "site_code", width: 12 },
        { header: "DC Name", key: "site_description", width: 35 },
        { header: "Articles", key: "article_count", width: 10 },
        { header: "SOH Qty", key: "total_soh_qty", width: 12 },
        { header: "SOH Value", key: "total_soh_value", width: 15 },
        { header: "Avg DROS", key: "avg_dros", width: 12 },
        { header: "Avg Days Cover", key: "avg_days_cover", width: 15 },
      ]
      summaryData.forEach((row) => {
        ws1.addRow({
          site_code: row.site_code,
          site_description: row.site_description,
          article_count: parseInt(row.article_count) || 0,
          total_soh_qty: parseInt(row.total_soh_qty) || 0,
          total_soh_value: parseFloat(row.total_soh_value) || 0,
          avg_dros: parseFloat(row.avg_dros) || 0,
          avg_days_cover: parseFloat(row.avg_days_cover) || 0,
        })
      })
      ws1.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
      })
      ws1.getColumn("total_soh_value").numFmt = "#,##0.00"

      // Detail sheet
      if (detailData.length > 0) {
        const ws2 = workbook.addWorksheet("DC Detail")
        ws2.columns = [
          { header: "DC Code", key: "site_code", width: 12 },
          { header: "DC Name", key: "site_description", width: 35 },
          { header: "Article Code", key: "article_code", width: 15 },
          { header: "Description", key: "articledescription", width: 35 },
          { header: "Brand", key: "brand", width: 18 },
          { header: "SOH Qty", key: "soh_qty", width: 10 },
          { header: "SOH Value", key: "soh_value", width: 15 },
          { header: "DROS", key: "dros_qty", width: 10 },
          { header: "Days Cover", key: "days_cover", width: 12 },
          { header: "Status", key: "site_article_status", width: 8 },
        ]
        detailData.forEach((row) => {
          ws2.addRow({
            site_code: row.site_code,
            site_description: row.site_description,
            article_code: row.article_code,
            articledescription: row.articledescription,
            brand: row.brand || "-",
            soh_qty: parseInt(row.soh_qty) || 0,
            soh_value: parseFloat(row.soh_value) || 0,
            dros_qty: parseFloat(row.dros_qty) || 0,
            days_cover: parseFloat(row.days_cover) || 0,
            site_article_status: row.site_article_status || "-",
          })
        })
        ws2.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
        })
        ws2.getColumn("soh_value").numFmt = "#,##0.00"
      }

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = `DC_Stock_${supplier}_${retailer}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Download error:", err)
    } finally {
      setDownloading(false)
    }
  }

  const hasData = summaryData.length > 0

  return (
    <Box>
      {/* Controls */}
      <Flex mb={4} gap={4} align="flex-end" wrap="wrap">
        {hasData && (
          <Button
            size="sm"
            leftIcon={<DownloadIcon />}
            colorScheme="teal"
            onClick={handleDownload}
            isLoading={downloading}
            loadingText="Downloading"
          >
            Download Report
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : !hasData ? (
        <Box textAlign="center" py={10}>
          <Text color="gray.500">No DC stock data found for this supplier/retailer</Text>
        </Box>
      ) : (
        <>
          {/* KPI Cards */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
            <Stat bg={kpiBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Total DC SOH Value</StatLabel>
              <StatNumber fontSize="lg">{formatCurrency(totalSohValue)}</StatNumber>
            </Stat>
            <Stat bg={kpiBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Total DC SOH Units</StatLabel>
              <StatNumber fontSize="lg">{formatNum(totalSohQty)}</StatNumber>
            </Stat>
            <Stat bg={kpiBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Warehouses / Articles</StatLabel>
              <StatNumber fontSize="lg">
                {summaryData.length} DC{summaryData.length !== 1 ? "s" : ""} / {totalArticles} lines
              </StatNumber>
            </Stat>
          </SimpleGrid>

          {/* Per-DC breakdown */}
          {summaryData.map((dc) => {
            const isExpanded = expandedDc === dc.site_code
            const dcDetail = getDetailForDc(dc.site_code)
            const pct = totalSohValue > 0 ? ((parseFloat(dc.total_soh_value) / totalSohValue) * 100).toFixed(1) : 0

            return (
              <Box
                key={dc.site_code}
                bg={cardBg}
                borderRadius="lg"
                border="1px"
                borderColor={borderColor}
                mb={3}
                boxShadow="sm"
              >
                {/* DC Header */}
                <Flex
                  p={3}
                  align="center"
                  justify="space-between"
                  cursor="pointer"
                  onClick={() => setExpandedDc(isExpanded ? null : dc.site_code)}
                  _hover={{ bg: hoverBg }}
                  borderRadius={isExpanded ? "lg lg 0 0" : "lg"}
                >
                  <Box>
                    <Flex align="center" gap={2}>
                      <Heading size="sm">{dc.site_description}</Heading>
                      <Badge colorScheme="gray" fontSize="xs">
                        {dc.site_code}
                      </Badge>
                    </Flex>
                    <Flex gap={4} mt={1} fontSize="xs" color="gray.500">
                      <Text>{dc.article_count} articles</Text>
                      <Text>SOH: {formatNum(dc.total_soh_qty)} units</Text>
                      <Text fontWeight="bold" color="teal.600">
                        {formatCurrency(dc.total_soh_value)}
                      </Text>
                      <Text>({pct}% of total)</Text>
                      {dc.avg_days_cover && (
                        <Text>Avg {parseFloat(dc.avg_days_cover).toFixed(0)} days cover</Text>
                      )}
                    </Flex>
                  </Box>
                  <IconButton
                    icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    variant="ghost"
                    size="sm"
                    aria-label="Toggle detail"
                  />
                </Flex>

                {/* DC Detail Table */}
                <Collapse in={isExpanded}>
                  <Box borderTop="1px" borderColor={borderColor} maxH="350px" overflowY="auto">
                    <Table size="sm" variant="simple">
                      <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                        <Tr>
                          <Th>Article</Th>
                          <Th>Brand</Th>
                          <Th isNumeric>SOH Qty</Th>
                          <Th isNumeric>SOH Value</Th>
                          <Th isNumeric>DROS</Th>
                          <Th isNumeric>Days Cover</Th>
                          <Th>Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {dcDetail.map((row, i) => (
                          <Tr key={i} _hover={{ bg: hoverBg }}>
                            <Td fontSize="xs" maxW="220px" isTruncated>
                              {row.articledescription}
                            </Td>
                            <Td fontSize="xs">{row.brand || "-"}</Td>
                            <Td isNumeric fontSize="xs">
                              {formatNum(row.soh_qty)}
                            </Td>
                            <Td isNumeric fontSize="xs" fontWeight="bold" color="teal.600">
                              {formatCurrency(row.soh_value)}
                            </Td>
                            <Td isNumeric fontSize="xs">
                              {parseFloat(row.dros_qty)?.toFixed(2) || "-"}
                            </Td>
                            <Td isNumeric fontSize="xs">
                              {parseFloat(row.days_cover)?.toFixed(0) || "-"}
                            </Td>
                            <Td fontSize="xs">
                              <Badge
                                colorScheme={row.site_article_status === "04" ? "green" : row.site_article_status ? "orange" : "gray"}
                                fontSize="2xs"
                              >
                                {row.site_article_status || "-"}
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Collapse>
              </Box>
            )
          })}
        </>
      )}
    </Box>
  )
}

export default DcStock
